import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';

interface BlogPostMetrics {
  totalGenerated: number;
  completedArticles: number;
  failedArticles: number;
  pendingArticles: number;
  loading: boolean;
  error: string | null;
}

export function useBlogPostMetrics(websiteId: string | null, enabled: boolean = true): BlogPostMetrics {
  const [metrics, setMetrics] = useState<BlogPostMetrics>({
    totalGenerated: 0,
    completedArticles: 0,
    failedArticles: 0,
    pendingArticles: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!websiteId || !enabled) {
      setMetrics(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchBlogPostMetrics = async () => {
      try {
        setMetrics(prev => ({ ...prev, loading: true, error: null }));

        const { data, error } = await supabase
          .from('generated_articles')
          .select('generation_status')
          .eq('website_id', websiteId);

        if (error) {
          throw error;
        }

        const totalGenerated = data?.length || 0;
        const completedArticles = data?.filter(article => article.generation_status === 'completed').length || 0;
        const failedArticles = data?.filter(article => article.generation_status === 'failed').length || 0;
        const pendingArticles = data?.filter(article => article.generation_status === 'pending' || article.generation_status === 'generating').length || 0;

        setMetrics({
          totalGenerated,
          completedArticles,
          failedArticles,
          pendingArticles,
          loading: false,
          error: null
        });

      } catch (err) {
        console.error('Error fetching blog post metrics:', err);
        setMetrics(prev => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to fetch blog post metrics'
        }));
      }
    };

    fetchBlogPostMetrics();
  }, [websiteId, enabled]);

  return metrics;
}


